export interface CategoryCopySource {
  name?: string | null;
  description?: string | null;
  region?: string | null;
  birth_year?: number | null;
  play_level?: string | null;
  level?: number | null;
  parent_category?: { name?: string | null } | null;
}

const clean = (value?: string | null) => value?.trim() || "";

const getPlaceFromName = (name: string) =>
  name
    .replace(/\s+Youth Baseball(?:\s+Forum)?$/i, "")
    .replace(/\s+Baseball(?:\s+Forum)?$/i, "")
    .trim();

export const getCategoryBrowseTitle = (category: CategoryCopySource): string => {
  const name = clean(category.name) || "Youth Baseball";
  return /forum$/i.test(name) ? `Browse ${name} Sections` : `Browse ${name} Forums`;
};

export const getCategoryDescription = (category: CategoryCopySource): string => {
  const savedDescription = clean(category.description);
  if (savedDescription) return savedDescription;

  const name = clean(category.name) || "this youth baseball forum";
  const details = [clean(category.region), category.birth_year ? `${category.birth_year} birth year` : "", clean(category.play_level)]
    .filter(Boolean)
    .join(", ");

  return details
    ? `Discuss ${name}, including teams, schedules, tryouts, tournaments, and player development for ${details}.`
    : `Discuss teams, leagues, tryouts, tournaments, coaching, and player development in ${name}.`;
};

export const getCategoryContext = (category: CategoryCopySource): string => {
  const name = clean(category.name) || "this forum";
  const parentName = clean(category.parent_category?.name);

  if (category.birth_year) {
    return `Connect with families and coaches following ${category.birth_year} players, compare local programs, and share timely baseball information.`;
  }

  if (category.play_level) {
    return `Compare ${category.play_level} programs, ask questions, and share practical experience with other youth baseball families and coaches.`;
  }

  if (category.region) {
    const place = getPlaceFromName(name) || category.region;
    return `Find local perspectives on leagues, teams, registration, development, and tournament opportunities throughout ${place}.`;
  }

  if (parentName) {
    return `Use this section of ${parentName} to ask questions, compare experiences, and help other youth baseball families make informed decisions.`;
  }

  return `Use ${name} to ask questions, compare experiences, and share useful information with youth baseball players, parents, and coaches.`;
};

export const getCategoryAboutContent = (category: CategoryCopySource): string => {
  const name = clean(category.name) || "Youth Baseball";
  const parentName = clean(category.parent_category?.name);
  const localFocus = category.region
    ? `Conversations focus on youth baseball in ${getPlaceFromName(name) || category.region}, including local organizations, seasonal schedules, team options, and events.`
    : `Conversations can cover recreational and travel baseball, age divisions, team selection, practices, equipment, tournaments, and player development.`;
  const community = parentName
    ? `This forum is part of ${parentName}. Share firsthand knowledge, ask specific questions, and help other baseball families understand their options.`
    : `Share firsthand knowledge, ask specific questions, and help other baseball families and coaches understand their options.`;

  return `This section brings together useful conversations for people involved in ${name}, from families exploring programs to coaches sharing practical experience.\n\n## What You Can Discuss\n\n${localFocus}\n\n## Join the ${name} Community\n\n${community}`;
};

export const getCategoryMetaDescription = (
  category: CategoryCopySource,
  forumName = "Minor Baseball Talks",
): string => {
  const name = clean(category.name) || "Youth Baseball";
  const location = clean(category.region);
  const focus = category.birth_year
    ? `${category.birth_year} players, teams, tryouts and tournaments`
    : category.play_level
      ? `${category.play_level} teams, coaching, tryouts and tournaments`
      : location
        ? `local leagues, teams, tryouts and tournaments across ${location}`
        : "teams, leagues, coaching, tryouts and tournaments";

  return `${name} forum for parents, coaches and players discussing ${focus} on ${forumName}.`;
};